import { Modal } from 'react-bootstrap'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { useCurrentUser } from '../../hooks/useCurrentUser'
import useSponsorPledgeMinimumCiv from '../../hooks/useSponsorPledgeMinimumCiv'
import Button from '../button'
import Icon from '../icon'
import PledgeHeader from './PledgeHeader'
import SponsorMessageForm from './SponsorMessageForm'
import SponsorPledgeForm from './SponsorPledgeForm'
import StripeSetupForm from '../StripeSetupForm'
import styles from '../../pages/events/sponsorship/sponsorship.module.scss'
import useApi from '../../hooks/useApi'
import civ from '../../constants/civ'
import { feeValue, formatAmount } from '../../lib/numbers'

interface SponsorPledgeModalProps {
  event?: any
  onClose: () => void
  pledgeAmount?: string
  show: boolean
}

type Step = 'pledge' | 'payment' | 'message' | 'success'

export default function SponsorPledgeModal(props: SponsorPledgeModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { business, fetchBusiness } = useCurrentUser()
  const { createSponsorshipPledge, getAdminFee } = useApi()

  const { pledgeEvent, minimumPledgeCiv } = useSponsorPledgeMinimumCiv({
    show: props.show,
    event: props.event,
  })
  const pledgePrefilledRef = useRef(false)

  const [pledgeAmount, setPledgeAmount] = useState<string>(
    props.pledgeAmount as string,
  )
  const [step, setStep] = useState<Step>('pledge')

  const [adminFee, setAdminFee] = useState(civ.empty)
  const [serviceFee, setServiceFee] = useState(civ.empty)
  const [currentFee, setCurrentFee] = useState(0)
  const [currentServiceFee, setCurrentServiceFee] = useState(0)
  const [stripePaymentMethodId, setStripePaymentMethodId] = useState<string | null>(null)

  const onSubmit = useCallback(
    async (message: string) => {
      const amount = Number.parseInt(String(pledgeAmount), 10)
      const pledgeData = {
        event_id: props.event?.id,
        stripe_payment_method_id: stripePaymentMethodId,
        civ_pledge: {
          amount,
          message_to_event_owner: message,
          civillos_admin_fee_id: adminFee.id,
          meta_admin_fee_id: serviceFee.id,
        },
      }
      const response = await createSponsorshipPledge(pledgeData)
      const error =
        //@ts-ignore
        response?.error?.response?.data?.error || response?.error?.message
      if (error) {
        alert(error)
      } else {
        setStep('success')
      }
    },
    [pledgeAmount, props.event, stripePaymentMethodId, adminFee, serviceFee],
  )

  const onPaymentSuccess = useCallback((paymentMethodId: string) => {
    setStripePaymentMethodId(paymentMethodId)
    setStep('message')
  }, [])

  const closeModal = useCallback(() => {
    pledgePrefilledRef.current = false
    setStep('pledge')
    setPledgeAmount('')
    setStripePaymentMethodId(null)
    props.onClose()
  }, [props.onClose])

  const handleOnClose = useCallback(() => {
    if (step === 'success') {
      closeModal()
    } else {
      const confirm = window.confirm(
        t('sponsorship.pledgeModal.confirmCloseMessage'),
      )
      if (confirm) {
        closeModal()
      }
    }
  }, [props.onClose, step])

  useEffect(() => {
    if (step === 'pledge') {
      fetchBusiness()
    }
  }, [step])

  useEffect(() => {
    getAdminFee().then((result: any) => {
      setAdminFee(result.data.current_fees.civillos_fee)
      setServiceFee(result.data.current_fees.meta_fee)
    })
  }, [])

  useEffect(() => {
    if (!!adminFee) {
      setCurrentFee(feeValue(pledgeAmount, adminFee))
    }
  }, [pledgeAmount, adminFee])

  useEffect(() => {
    if (!!serviceFee) {
      setCurrentServiceFee(feeValue(pledgeAmount, serviceFee))
    }
  }, [pledgeAmount, serviceFee])

  useEffect(() => {
    if (!props.show) {
      pledgePrefilledRef.current = false
    }
  }, [props.show])

  useEffect(() => {
    if (
      !props.show ||
      step !== 'pledge' ||
      pledgePrefilledRef.current ||
      (pledgeAmount ?? '') !== '' ||
      minimumPledgeCiv == null ||
      minimumPledgeCiv <= 0
    ) {
      return
    }
    setPledgeAmount(String(minimumPledgeCiv))
    pledgePrefilledRef.current = true
  }, [props.show, step, pledgeAmount, minimumPledgeCiv])

  return (
    <Modal
      onHide={handleOnClose}
      show={props.show}
      contentClassName={styles.modalBackground}
    >
      {/* // style={{ width: '550px', height: '850px' }} */}
      <Modal.Header closeButton style={{ border: 'none' }}></Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        {step === 'pledge' ? (
          <SponsorPledgeForm
            business={business}
            event={pledgeEvent}
            onNext={() => setStep('payment')}
            pledgeAmount={pledgeAmount}
            setPledgeAmount={setPledgeAmount}
            adminFee={adminFee}
            serviceFee={serviceFee}
            currentAdminFee={currentFee}
            currentServiceFee={currentServiceFee}
            minimumPledgeCiv={minimumPledgeCiv}
          />
        ) : null}
        {step === 'payment' ? (
          <div className={styles.section}>
            <div className="mb-3">
              <h2>
                <b>{t('sponsorship.pledgeModal.paymentStep.title')}</b>
              </h2>
            </div>
            <StripeSetupForm
              onSuccess={onPaymentSuccess}
              onCancel={() => setStep('pledge')}
            />
          </div>
        ) : null}
        {step === 'message' ? (
          <SponsorMessageForm
            event={pledgeEvent}
            onSubmit={onSubmit}
            pledgeAmount={pledgeAmount}
            currentFee={currentFee}
            currentServiceFee={currentServiceFee}
          />
        ) : null}

        {step === 'success' ? (
          <div className={styles.section}>
            <div className="mb-3">
              <h2>
                <b>{t('sponsorship.pledgeModal.successTitle')}</b>
              </h2>
            </div>
            <div className={styles.successIconContainer}>
              <Icon name="check-circle" />
            </div>
            <div className="mb-3">{moment().format('MMMM Do, YYYY')}</div>
            <div className="mb-3">Sent to: {pledgeEvent.user.name}</div>

            <div className="mb-3">
              <PledgeHeader event={pledgeEvent} pledgeAmount={pledgeAmount} />
            </div>

            <div className="mb-3">
              <div className={styles.terms}>
                {t('sponsorship.pledgeModal.successFinePrint', {
                  service_fee: formatAmount(currentFee, true),
                })}
              </div>
            </div>

            <div className="mb-3">
              <Button
                onClick={() => {
                  navigate('/dashboard')
                }}
                className="w-100 mb-4"
              >
                {t('sponsorship.pledgeModal.goToDashboard')}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  )
}
